// @flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import Button from 'app/common/react/components/growth/unauth/Button/Button';
import CONST from 'app/common/lib/Const';
import gapi from 'app/common/lib/Gapi';
import i18n from 'app/common/lib/i18n';
import login from 'app/common/react/components/growth/unauth/signup/UserLogin';
import loginUtil from 'app/www-unauth/lib/loginUtil';
import PropTypes from 'prop-types';
import templateConst from 'app/common/lib/templateConst';
import Tooltip from 'app/common/react/components/growth/unauth/signup/Tooltip/Tooltip';
import trackRegisterAction from 'app/common/lib/util/trackRegisterAction';
import type { GoogleApiResponse } from 'app/packages/pinterest-authentication/google';
import type { LoginCreds } from 'app/packages/pinterest-flow-types/authentication';
import type { SignupCredentialsAndLoggingData } from 'app/packages/pinterest-flow-types/registration';
import type { State as ReduxState } from 'app/common/redux/reducers/types';
import type { Viewer } from 'app/packages/pinterest-flow-types/viewer';
import UserRegistration from 'app/common/react/components/growth/unauth/signup/UserRegistration';
import WindowsPWAWebView from 'app/common/react/components/growth/unauth/signup/GoogleConnectButton/WindowsPWAWebView';
import { activateGoogleSignInExperiment } from 'app/common/react/components/growth/unauth/signup/GoogleConnectButton/googleSignInExperimentUtil';
import { addUsersLoggedIn } from 'app/common/lib/MultipleAccountsUtils';
import { compose } from 'redux';
import { DefaultFontFamily } from 'app/common/react/components/common/FontFamily';
import { initGoogleAuth2SDK } from 'app/packages/pinterest-authentication/google';
import { showError } from 'app/common/react/components/growth/unauth/lib/ErrorUtils';
import { SingleTreatmentExp } from 'app/common/lib/experiment';
import { track } from 'app/common/lib/react/analytics';
import { withRouter } from 'react-router';
import withUnauthSeoExperiments, {
  type UnauthSeoExperiments,
} from 'app/common/lib/react/withUnauthSeoExperiments';
import {
  registrationCollectsAgeMarkets,
  registrationRequiresAgeEN,
  registrationRequiresAgeGDPR,
  SIGNUP_METHOD,
} from 'app/packages/pinterest-authentication/registration'; // Needs work
import {
  withRequestContext,
  type RequestContextType,
} from 'app/common/react/components/RequestContext';

type StyleOverrides = {|
  button?: {},
  icon?: {},
  iconContainer?: {},
  text?: {},
|};

type OwnProps = {|
  buttonText?: string,
  container?: string,
  disablePostSignupNextUrl: boolean,
  inline: boolean,
  isAccountSwitch?: boolean,
  nextUrlParam: string,
  onSocialConnectVerified?: (
    network: string,
    userData: Object,
    credentialsAndLoggingData: SignupCredentialsAndLoggingData,
  ) => void,
  onLoginWithMfa?: (creds: LoginCreds, Object) => void,
  onLoginWithSuspended?: () => void,
  onLoginWithVoluntarilyDeactivated?: (Object) => void,
  preventRegister: (string) => void,
  requestContext: RequestContextType,
  seoUnauthExperiments: UnauthSeoExperiments,
  showLoginWelcomeLoadingFn?: (boolean) => void,
  showResponseMessageModal?: (isSignup: boolean) => void,
  showSignupWelcomeLoadingFn: (boolean) => void,
  styleOverrides?: StyleOverrides,
  tooltip: string,
|};

type ReduxProps = {|
  viewer: Viewer,
|};

type Props = OwnProps & ReduxProps;

type State = {
  showGoogleSigninWebview: boolean,
};

// https://developers.google.com/identity/sign-in/web/reference#googleusergetauthresponseincludeauthorizationdata
export type GoogleAuth2Response = {
  access_token: string,
  id_token: string,
  scope: string,
  expires_in: number,
  first_issued_at: number,
  expires_at: number,
};

const styles = {
  button: {
    padding: '1px',
    marginRight: '0px',
    textAlign: 'left',
    backgroundClip: 'padding-box',
    display: 'block',
    transition: 'opacity .2s linear',
    position: 'relative',
    outline: 'none',
    width: '100%',
    marginTop: '0px',
    backgroundColor: '#4285f4',
    height: '40px',
    borderRadius: '2px',
  },
  icon: {
    borderRadius: '2px',
    backgroundColor: '#fff',
    display: 'inline-block',
    height: '25px',
    marginLeft: '7px',
    textAlign: 'center',
    width: '25px',
  },
  iconContainer: {
    position: 'relative',
    margin: 'auto',
    marginTop: '2px',
  },
  text: {
    color: '#fff',
    display: 'inline-block',
    fontFamily: DefaultFontFamily,
    position: 'absolute',
    textAlign: 'center',
    top: '50%',
    transform: 'translateY(-50%)',
    verticalAlign: 'top',
    WebkitFontSmoothing: 'auto', // 'auto' matches Facebook's iframe connect button 10/20/17
    width: '83%',
  },
  tooltip: {
    top: '12px',
    left: '100%',
    position: 'absolute',
  },
};

const GPLUS_REDIRECT_URI = 'postmessage';
// TODO (vincent): Look into adding profile scope to get profile gender
// https://developers.google.com/+/web/api/rest/latest/people/get#response
const GPLUS_SCOPE_PROFILE_EMAIL = 'profile email';

export const GOOGLE_IDENTIFIER: 'google' = 'google';

const CONNECT_TYPE_SIGNIN_RENDER = 'signin_render';
const CONNECT_TYPE_SIGNIN_AUTH2 = 'signin_auth2';

const isWindowsPWA = () => typeof window !== 'undefined' && typeof window.Windows !== 'undefined';

export class GoogleConnectButton extends Component<Props, State> {
  static defaultProps = {
    inline: false,
  };

  state: State = {
    showGoogleSigninWebview: false,
  };

  componentDidMount() {
    if (typeof window !== 'undefined') {
      if (window.googleOnSignInCallbacks) {
        // Implementing a first in last out stack for the callbacks passed to the google login api object
        // When there are multiple <GoogleConnectButton/> instances on the page only the callback from the
        // first button mounted will be called regardless of which instance of the button is clicked.
        // This means that if we click a second instance of the GoogleConnectButton where we intend to
        // alter the behavior after successful google authentication based on the props passed to that instance
        // we will instead trigger the behavior specified by the props of the first button.
        // By instead grabbing the last function in the window.googleOnSignInCallback we can execute the behavior
        // intended by the most recently mounted button
        window.googleOnSignInCallbacks.push(this.onSignInCallback);
      } else {
        window.googleOnSignInCallbacks = [this.onSignInCallback];
      }
    }

    this.readyForConnect = false;
    this.autoLogin = false;

    if (!isWindowsPWA()) {
      gapi.get((_gapi) => {
        this._googleCallback(_gapi);
      });
    }
    window.googleConnectButtonContainer = this.props.container ? this.props.container : '';
  }

  componentWillUnmount() {
    if (typeof window !== 'undefined' && window.googleOnSignInCallbacks) {
      const idx = window.googleOnSignInCallbacks.indexOf(this.onSignInCallback);
      if (idx >= 0) {
        window.googleOnSignInCallbacks.splice(idx, 1);
      }
    }
    delete window.googleConnectButtonContainer;
  }

  /**
   * Try to create a UserSessionResource with certain credentials.
   *
   * @param {!Object} authResult
   *  id_token gplus token to identify the user
   *  status
   *  gplus one time code needed on register
   * @param {boolean} isAutologin
   *  if the login was triggered by autologin, for logging purposes
   * @param {!Object} gapi
   *  The object used to make google api calls
   * @param {!string} connectType
   *  Represents the type of API used to attempt Google connect
   */
  onSignInCallback = (
    authResult: GoogleApiResponse,
    isAutologin: boolean,
    gapi: Object,
    connectType: string = CONNECT_TYPE_SIGNIN_RENDER,
  ) => {
    const { requestContext, showLoginWelcomeLoadingFn } = this.props;
    const data = {
      gplus_id_token: authResult.id_token,
      gplus_access_token: authResult.access_token,
      gplus_expires_at: authResult.expires_at,
    };
    const status = authResult.status;

    // Check if the user has succesfully signed in to gplus then
    // actually log the user into Pinterest.
    if (status.signed_in) {
      if (showLoginWelcomeLoadingFn) {
        const inGoogleLoginWelcomeExp = new SingleTreatmentExp(
          requestContext,
          'web_login_welcome_google',
        ).enabled();
        requestContext.experiments.flush();
        if (inGoogleLoginWelcomeExp) {
          showLoginWelcomeLoadingFn(true);
        }
      }
      trackRegisterAction(`unauth_web.google_connect.success.${connectType}`);
      this.login(data).then(
        (response = {}) => {
          if (
            this.props.isAccountSwitch &&
            response.resource_response &&
            response.resource_response.data &&
            response.resource_response.data.user
          ) {
            const user = response.resource_response.data.user;
            addUsersLoggedIn(user, GOOGLE_IDENTIFIER, this.props.container);
          }
          if (isAutologin) {
            trackRegisterAction('autologin.google');
            trackRegisterAction('autologin.google_connect_button');
          }
          this.onLoginSuccess();
        },
        (error) => {
          if (showLoginWelcomeLoadingFn) {
            showLoginWelcomeLoadingFn(false);
          }
          if (error.api_error_code === CONST.LOGIN_VOLUNTARILY_DEACTIVATED_USER) {
            if (this.props.onLoginWithVoluntarilyDeactivated) {
              this.props.onLoginWithVoluntarilyDeactivated(error);
            }
          } else if (error.api_error_code === CONST.LOGIN_MFA_REQUIRED) {
            // redirect to the LoginMfa screen
            if (this.props.onLoginWithMfa) {
              this.props.onLoginWithMfa(data, error.data);
            }
          } else if (error.api_error_code === CONST.LOGIN_USER_NOT_FOUND) {
            if (this.props.preventRegister) {
              this.props.preventRegister(GOOGLE_IDENTIFIER);
              return;
            }
            this.onLoginError(error, authResult, gapi);
          } else {
            if (error && error.api_error_code) {
              trackRegisterAction(
                `google_connect_button.failed_login.${error.api_error_code}.${connectType}`,
              );
            }
            error.api_error_code === CONST.POLICY_VIOLATION_USER && this.props.onLoginWithSuspended
              ? this.props.onLoginWithSuspended()
              : showError(
                  i18n._(
                    "Sorry, we can't log you in.",
                    'error message when user failed to login caused by suspend/deactivated etc.',
                  ),
                );
          }
        },
      );
    } else {
      const error = authResult ? authResult.error : 'unknown';
      trackRegisterAction(`unauth_web.google_connect.failure.${String(error)}.${connectType}`);
    }
  };

  onLoginSuccess = () => {
    const { showResponseMessageModal } = this.props;
    if (showResponseMessageModal) {
      showResponseMessageModal(false);
    } else {
      loginUtil.handleRedirect(this.props.nextUrlParam || window.location.pathname);
    }
  };

  /**
   * If login fails then register the user
   *
   * Sets one_time_code in SessionStorage for it to be used again
   * in UserRegister. This will be sent to do the server
   * on registration and exchanged for access/refresh tokens
   *
   * @param {!Object} errorData login failure data
   * @param {!Object} authResult
   *  code gplus one time code
   * @param {!Object} gapi
   *  The object used to make google api calls
   */
  onLoginError(errorData: Object, authResult: GoogleApiResponse, gapi: Object) {
    const {
      onSocialConnectVerified,
      requestContext,
      showSignupWelcomeLoadingFn,
      showResponseMessageModal,
    } = this.props;

    const credentials = {
      gplus_code: authResult.code,
      gplus_id_token: authResult.id_token,
      gplus_redirect_uri: GPLUS_REDIRECT_URI,
      ...(typeof authResult.first_name !== 'undefined'
        ? { gplus_first_name: authResult.first_name }
        : {}),
    };

    const loggingData = {
      container: window.googleConnectButtonContainer,
      hybridTier: this.context.hybridTier,
      page: this.context.pageType,
    };

    const recapToken = null;

    if (onSocialConnectVerified && this.shouldRequireAge()) {
      onSocialConnectVerified(SIGNUP_METHOD.GOOGLE, {}, { ...credentials, ...loggingData });
      return;
    }

    // todo(sophia) We need to flush experiment triggering immediately (current experiment client default wraps
    // activate resource call in a queued resource call with 5 second delay). Longterm soln is to support immediate
    // flush in ExperimentChecker class.
    // https://paper.dropbox.com/doc/Web-Welcome-Loading-Screen-Experiments-Triggering-Issues--AMlvl51RMVYopimH6DjeJ8EzAg-BwroITTNabE6ckNqNRoqJ
    const inWelcomeLoadingExp = new SingleTreatmentExp(
      requestContext,
      'web_unauth_gplus_welcome_v2',
    ).enabled();
    requestContext.experiments.flush();
    if (!showResponseMessageModal && showSignupWelcomeLoadingFn && inWelcomeLoadingExp) {
      showSignupWelcomeLoadingFn(true);
    }

    UserRegistration.register({ ...credentials, ...loggingData, recapToken }).then(
      this.handleSignupSuccess,
      this.handleSignupFailure,
    );
  }

  readyForConnect: boolean;
  autoLogin: boolean;
  googleButton: HTMLElement;

  _googleCallback(_gapi: Object) {
    if (activateGoogleSignInExperiment(this.props.requestContext)) {
      // No-op. Signin occurs in handleGoogleConnectButtonClick. Remove
      // this function once experiment is complete.
    } else {
      // Google+
      _gapi.signin.render(this.googleButton, {
        scope: GPLUS_SCOPE_PROFILE_EMAIL,
        clientid: templateConst.settings.GPLUS_CLIENT_ID,
        apppackagename: templateConst.settings.GPLUS_APP_PACKAGE_NAME,
        redirecturi: GPLUS_REDIRECT_URI,
        accesstype: 'offline',
        cookiepolicy: 'single_host_origin',
        callback: function(authResult: GoogleApiResponse) {
          if (this.readyForConnect) {
            if (
              window.googleOnSignInCallbacks &&
              window.googleOnSignInCallbacks[window.googleOnSignInCallbacks.length - 1]
            ) {
              window.googleOnSignInCallbacks[window.googleOnSignInCallbacks.length - 1](
                authResult,
                this.autoLogin,
                _gapi,
              );
            }
          }
          this.readyForConnect = true;
          this.autoLogin = false;
        }.bind(this),
      });
    }
  }

  shouldRequireAge = () => {
    const { country } = this.context;
    const { requestContext } = this.props;
    const ageIsMandatory =
      registrationRequiresAgeGDPR(country) || registrationRequiresAgeEN(country);
    if (ageIsMandatory) {
      return true;
    }
    const isInAgeCollectionMarkets =
      registrationCollectsAgeMarkets(country) &&
      new SingleTreatmentExp(requestContext, 'web_age_collection_expansion_markets').enabled();
    const isInAgeCollectionRow = new SingleTreatmentExp(
      requestContext,
      'web_age_collection_expansion_row',
    ).enabled();
    return isInAgeCollectionMarkets || isInAgeCollectionRow;
  };

  contextLogSignupLoginEvent(event: string) {
    const { viewer } = this.props;
    if (viewer.isAuth) {
      track(event, {
        login_state: viewer.isLimitedLogin
          ? PCONST.LoginStates.LIMITED
          : PCONST.LoginStates.DEFAULT,
        signup_login_method: PCONST.SignupLoginMethods.GPLUS,
      });
    } else {
      track(event, {
        signup_login_method: PCONST.SignupLoginMethods.GPLUS,
      });
    }
  }

  handleGoogleConnectButtonClick = () => {
    gapi.get((_gapi) => {
      initGoogleAuth2SDK((action) =>
        trackRegisterAction(`unauth_web.google_connect.init.${action}`),
      )
        .then(
          (googleAuth) => {
            if (isWindowsPWA()) {
              this.setState({
                showGoogleSigninWebview: true,
              });
            }
            if (activateGoogleSignInExperiment(this.props.requestContext)) {
              trackRegisterAction(`unauth_web.google_connect.init.${CONNECT_TYPE_SIGNIN_AUTH2}`);
              googleAuth
                .signIn({
                  scope: GPLUS_SCOPE_PROFILE_EMAIL,
                })
                .then(
                  (response) => {
                    trackRegisterAction(
                      `unauth_web.google_connect.success.${CONNECT_TYPE_SIGNIN_AUTH2}`,
                    );

                    const tokenData = response.getAuthResponse();

                    // The code is a required field for the v3/register/gplus API but the id_token
                    // is sufficient so an empty string is an acceptable value.
                    const codeData = { code: '' };

                    // TODO (javery) remove 'signed_in' after shipping new Google Sign-in API
                    const responseStatus = { status: { signed_in: true } };

                    const authResult = {
                      ...tokenData,
                      ...codeData,
                      ...responseStatus,
                    };

                    this.onSignInCallback(authResult, false, _gapi, CONNECT_TYPE_SIGNIN_AUTH2);
                  },
                  ({ error }) => {
                    trackRegisterAction(
                      `unauth_web.google_connect.failure.${String(
                        error,
                      )}.${CONNECT_TYPE_SIGNIN_AUTH2}`,
                    );
                  },
                );
            }
          },
          (error) => {
            trackRegisterAction('unauth_web.google_connect.error.auth2_sdk_init_error');
          },
        )
        .catch((error) => {
          trackRegisterAction(`unauth_web.gapi_auth2_load.error.${error || 'unknown'}`);
          showError(
            i18n._(
              'Sorry, you need to select an account if you want to log in with Google.',
              'error message when user failed to login/signup because they did not complete the Google step.',
            ),
          );
        });
    });
  };

  handleOnDimisssWindowsPWAWebview = () => {
    this.setState({ showGoogleSigninWebview: false });
  };

  handleOnErrorWindowsPWAWebview = () => {
    this.setState({ showGoogleSigninWebview: false });
    showError(
      i18n._(
        'Sorry, there was an error signing in with Google.',
        'error message when user failed to login/signup using Google sign-in.',
      ),
    );
  };

  handleSignupSuccess = () => {
    const { disablePostSignupNextUrl, nextUrlParam, showResponseMessageModal } = this.props;
    this.contextLogSignupLoginEvent(PCONST.thrift.EventType.SIGNUP_SUCCESS);
    if (showResponseMessageModal) {
      showResponseMessageModal(true);
    } else {
      loginUtil.handleSignupRedirect(nextUrlParam, disablePostSignupNextUrl);
    }
  };

  handleSignupFailure = () => {
    this.contextLogSignupLoginEvent(PCONST.thrift.EventType.SIGNUP_FAILURE);
  };

  /**
   * Modeled off of User.js - login()
   */
  login(creds: LoginCreds) {
    const loggingData = {
      container: window.googleConnectButtonContainer,
      hybridTier: this.context.hybridTier,
      page: this.context.pageType,
    };
    return login(creds, loggingData, this.props.seoUnauthExperiments, this.props.isAccountSwitch);
  }

  logo(size: number) {
    return (
      <svg height={size} viewBox="0 0 512 512" width={size} xmlns="http://www.w3.org/2000/svg">
        <g fill="none" fillRule="evenodd">
          <path
            d={
              'M482.56 261.36c0-16.73-1.5-32.83-4.29-48.27H256v91.29h127.01c-5.47 29.5-22.1 54.49-47.09 71.23v59.2' +
              '1h76.27c44.63-41.09 70.37-101.59 70.37-173.46z'
            }
            fill="#4285f4"
          />
          <path
            d={
              'M256 492c63.72 0 117.14-21.13 156.19-57.18l-76.27-59.21c-21.13 14.16-48.17 22.53-79.92 22.53-61.4' +
              '7 0-113.49-41.51-132.05-97.3H45.1v61.15c38.83 77.13 118.64 130.01 210.9 130.01z'
            }
            fill="#34a853"
          />
          <path
            d={
              'M123.95 300.84c-4.72-14.16-7.4-29.29-7.4-44.84s2.68-30.68 7.4-44.84V150.01H45.1C29.12 181.87 20 217.9' +
              '2 20 256c0 38.08 9.12 74.13 25.1 105.99l78.85-61.15z'
            }
            fill="#fbbc05"
          />
          <path
            d={
              'M256 113.86c34.65 0 65.76 11.91 90.22 35.29l67.69-67.69C373.03 43.39 319.61 20 256 20c-92.25 0-17' +
              '2.07 52.89-210.9 130.01l78.85 61.15c18.56-55.78 70.59-97.3 132.05-97.3z'
            }
            fill="#ea4335"
          />
          <path d="M20 20h472v472H20V20z" />
        </g>
      </svg>
    );
  }

  renderTooltip = (tooltip: string) => {
    if (!tooltip) {
      return null;
    }

    return (
      <div style={styles.tooltip}>
        <Tooltip message={tooltip} />
      </div>
    );
  };

  render() {
    const { buttonText, inline, styleOverrides } = this.props;
    const { showGoogleSigninWebview } = this.state;
    let buttonStyles = styles.button;
    if (inline) {
      buttonStyles = { ...buttonStyles, width: '48%', display: 'inline-block' };
    }

    if (styleOverrides && styleOverrides.button) {
      buttonStyles = { ...buttonStyles, ...styleOverrides.button };
    }

    const iconStyles = { ...styles.icon, ...((styleOverrides && styleOverrides.icon) || {}) };
    const textStyles = { ...styles.text, ...((styleOverrides && styleOverrides.text) || {}) };

    const googleButtonText = buttonText || i18n._('Continue with Google');

    // TODO: use button spinner
    return (
      <React.Fragment>
        <Button
          id="googleConnectButton"
          buttonRef={(ref) => {
            this.googleButton = ref;
          }}
          className="GoogleConnectButton"
          onClick={this.handleGoogleConnectButtonClick}
          styleOverrides={buttonStyles}
          type="button"
        >
          <div style={iconStyles}>
            <div style={styles.iconContainer}>{this.logo(20)}</div>
          </div>
          <span style={textStyles}>{googleButtonText}</span>
          {this.renderTooltip(this.props.tooltip)}
        </Button>
        {showGoogleSigninWebview &&
          isWindowsPWA() && (
            <WindowsPWAWebView
              onDismiss={this.handleOnDimisssWindowsPWAWebview}
              onError={this.handleOnErrorWindowsPWAWebview}
              onSignInCallback={this.onSignInCallback}
            />
          )}
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state: ReduxState): ReduxProps => ({
  viewer: state.viewer,
});

GoogleConnectButton.contextTypes = {
  country: PropTypes.string,
  hybridTier: PropTypes.string,
  pageType: PropTypes.string,
};

export default compose(
  withRequestContext,
  connect(mapStateToProps),
  withUnauthSeoExperiments,
  withRouter,
)(GoogleConnectButton);
